import { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline";
import { useInViewReveal } from "@/hooks/useInViewReveal";

const faqCount = 15;
const faqKeys = Array.from({ length: faqCount }, (_, i) => i + 1);

export const FaqSection = () => {
  const [t] = useTranslation("homeowners");
  const { ref: sectionRef, hasRevealed } = useInViewReveal({ threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) =>
    setOpenIndex((prev) => (prev === index ? null : index));

  return (
    <section
      id="frequent-qa"
      ref={sectionRef}
      className="w-full bg-[#4f4f4b] px-6 sm:px-12 lg:px-24 py-16 lg:py-32"
    >
      <div
        className={classNames(
          "flex flex-col lg:flex-row gap-12 lg:gap-16 transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {/* Left column — title + CTA */}
        <div className="flex flex-col gap-8 lg:gap-10 lg:w-[30%] flex-shrink-0">
          <h3 className="font-minion text-white leading-tight text-[clamp(1.5rem,0.8rem+3vw,3.75rem)]">
            {t("Faq.Title")}
          </h3>
          <a
            href="/contact"
            className="w-fit border border-white text-white font-montserrat text-[clamp(0.875rem,0.7rem+0.5vw,1.25rem)] px-8 lg:px-12 py-3 lg:py-4 rounded-xl transition-all duration-[180ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] hover:bg-white hover:text-[#4f4f4b]"
          >
            {t("Faq.Cta")}
          </a>
        </div>

        {/* Right column — accordion */}
        <div className="flex flex-col gap-3 lg:gap-4 flex-1">
          {faqKeys.map((num) => {
            const isOpen = openIndex === num;
            return (
              <div
                key={num}
                className="bg-[#f9f9f6] rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => toggle(num)}
                  className="w-full flex items-center justify-between gap-4 px-6 lg:px-8 py-4 lg:py-5 text-left"
                >
                  <span className="font-montserrat font-semibold text-[#1a1a1a] text-[clamp(0.8rem,0.65rem+0.4vw,1.125rem)]">
                    {t(`Faq.Q${num}`)}
                  </span>
                  {isOpen ? (
                    <MinusIcon className="w-5 h-5 lg:w-6 lg:h-6 text-[#1a1a1a] flex-shrink-0" />
                  ) : (
                    <PlusIcon className="w-5 h-5 lg:w-6 lg:h-6 text-[#1a1a1a] flex-shrink-0" />
                  )}
                </button>

                <div
                  className={classNames(
                    "grid transition-[grid-template-rows] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
                    {
                      "grid-rows-[1fr]": isOpen,
                      "grid-rows-[0fr]": !isOpen,
                    }
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 lg:px-8 pb-5 lg:pb-6 font-montserrat font-normal text-[#444] leading-relaxed text-[clamp(0.75rem,0.6rem+0.35vw,1rem)]">
                      {t(`Faq.A${num}`)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
